import React, { useEffect, useState, useMemo, useCallback, useRef } from "react"
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Tooltip as LeafletTooltip,
  useMap,
} from "react-leaflet"
import "leaflet/dist/leaflet.css"
import L from "leaflet"
import "leaflet.heat"
import {
  Package,
  DollarSign,
  Truck,
  AlertCircle,
  MapPin,
  Calendar,
  Filter,
  TrendingUp,
  Layers,
} from "lucide-react"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts"
import { format, subDays, startOfDay, endOfDay } from "date-fns"

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

// --- Constants & Config ---
const SRI_LANKA_BOUNDS = [
  [5.8, 79.6],
  [9.9, 81.9],
]
const INITIAL_CENTER = [7.8731, 80.7718]
const INITIAL_ZOOM = 8
const MIN_ZOOM_LEVEL = 7

const BI_COLOR_SCALE = {
  low: "#FFE5E5",      // light red/pink
  mediumLow: "#FFB3B3",
  medium: "#FF8080",
  mediumHigh: "#FF4D4D",
  high: "#FF0000",     // strong red
}
// Alternative Blue Scale for more professional look
const BLUE_SCALE = {
  low: "#E0F2FE",      // sky-100
  mediumLow: "#7DD3FC",// sky-300
  medium: "#38BDF8",   // sky-400
  mediumHigh: "#0EA5E9",// sky-500
  high: "#0284C7",     // sky-600
}

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"]

// --- Helper Components ---

const HeatmapLayer = ({ points }) => {
  const map = useMap()
  const heatLayerRef = useRef(null)

  useEffect(() => {
    if (!map || !points || points.length === 0) return

    // Points format: [lat, lng, intensity]
    // Intensity should be normalized 0-1 usually, but leaflet.heat handles raw values if max is set options.
    // However, it's safer to normalize or pass max in options.

    // Normalize intensity to 0-1 for better gradient
    const maxVal = Math.max(...points.map(p => p[2]), 1)
    const normalizedPoints = points.map(p => [p[0], p[1], p[2] / maxVal])

    if (!heatLayerRef.current) {
      heatLayerRef.current = L.heatLayer(normalizedPoints, {
        radius: 25,
        blur: 15,
        maxZoom: 10,
        max: 1.0,
        gradient: {
          0.4: 'blue',
          0.6: 'cyan',
          0.7: 'lime',
          0.8: 'yellow',
          1.0: 'red'
        }
      }).addTo(map)
    } else {
      heatLayerRef.current.setLatLngs(normalizedPoints)
    }

    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current)
        heatLayerRef.current = null
      }
    }
  }, [map, points])

  return null
}


const StatCard = ({ title, value, icon, subtext, colorClass = "text-blue-600", bgClass = "bg-blue-100" }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-start space-x-4 hover:shadow-md transition-shadow">
    <div className={`p-3 rounded-lg ${bgClass}`}>
      {React.cloneElement(icon, { className: `w-6 h-6 ${colorClass}` })}
    </div>
    <div>
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <h3 className="text-2xl font-bold text-gray-800 mt-1">{value}</h3>
      {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
    </div>
  </div>
)

const FilterSelect = ({ label, value, onChange, options }) => (
  <div className="flex flex-col">
    <label className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border bg-gray-50"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
)

// --- Main Component ---

const OrderHeatMap = () => {
  // Data State
  const [heatmapData, setHeatmapData] = useState([])
  const [trendData, setTrendData] = useState([])
  const [geoJsonData, setGeoJsonData] = useState(null)

  // UI State
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [mapMode, setMapMode] = useState("choropleth") // 'choropleth' or 'heatmap'

  // Filters
  const [metric, setMetric] = useState("orderCount")
  const [dateRange, setDateRange] = useState("30") // days
  const [customStart, setCustomStart] = useState("")
  const [customEnd, setCustomEnd] = useState("")

  // --- Data Fetching ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        // Calculate dates
        let start, end
        if (dateRange === "custom") {
          start = customStart ? new Date(customStart) : subDays(new Date(), 30)
          end = customEnd ? new Date(customEnd) : new Date()
        } else {
          end = new Date()
          start = subDays(end, parseInt(dateRange))
        }

        const queryFn = (path) => {
          const url = new URL(`${BASE_URL}${path}`)
          url.searchParams.append("startDate", start.toISOString())
          url.searchParams.append("endDate", end.toISOString())
          return fetch(url.toString()).then(res => res.ok ? res.json() : Promise.reject(res.statusText))
        }

        const [mapRes, trendRes, geoRes] = await Promise.all([
          queryFn("/api/dashboard/order-heatmap"),
          queryFn("/api/dashboard/dashboard-trends"),
          // GeoJSON only needs to be fetched once really, but simple to keep here for now or memoize if needed
          fetch(`${BASE_URL}/api/cities/geojson`).then(res => res.ok ? res.json() : Promise.reject("GeoJSON Error"))
        ])

        setHeatmapData(mapRes)
        setTrendData(trendRes)
        setGeoJsonData(geoRes)

      } catch (err) {
        console.error("Dashboard Load Error:", err)
        setError("Failed to load dashboard data. Please check connection.")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [dateRange, customStart, customEnd])

  // --- Derived State (Stats) ---
  const stats = useMemo(() => {
    const totalOrders = heatmapData.reduce((acc, curr) => acc + (curr.orderCount || 0), 0)
    const totalSales = heatmapData.reduce((acc, curr) => acc + (curr.totalSales || 0), 0)
    const avgOrderValue = totalOrders > 0 ? Math.round(totalSales / totalOrders) : 0

    // Calculate simple growth (mock logic for now as we'd need prev period data for real growth)
    return { totalOrders, totalSales, avgOrderValue }
  }, [heatmapData])

  // --- Chart Data Preparation ---
  const districtData = useMemo(() => {
    const grouped = heatmapData.reduce((acc, curr) => {
      const dist = curr.district || "Unknown"
      if (!acc[dist]) acc[dist] = { name: dist, value: 0, sales: 0 }
      acc[dist].value += curr.orderCount || 0
      acc[dist].sales += curr.totalSales || 0
      return acc
    }, {})

    return Object.values(grouped).sort((a, b) => b.value - a.value).slice(0, 5)
  }, [heatmapData])

  const topCities = useMemo(() => {
    return [...heatmapData].sort((a, b) => (b.totalSales || 0) - (a.totalSales || 0)).slice(0, 10)
  }, [heatmapData])

  const trendChartData = useMemo(() => {
    return trendData.map(d => ({
      date: format(new Date(d._id), "MMM dd"),
      Orders: d.orders,
      Sales: d.sales
    }))
  }, [trendData])

  // --- Map Styling ---
  const getFeatureStyle = useCallback((feature) => {
    const cityName = feature.properties.name
    const dataItem = heatmapData.find(d => d._id?.toLowerCase() === cityName?.toLowerCase())
    const value = dataItem ? dataItem[metric] : 0

    // Normalize value for coloring (simple linear scale relative to max in dataset)
    const maxVal = Math.max(...heatmapData.map(d => d[metric] || 0), 1)
    const ratio = value / maxVal

    let fillColor = "transparent"
    if (value > 0) {
      if (ratio > 0.8) fillColor = BLUE_SCALE.high
      else if (ratio > 0.6) fillColor = BLUE_SCALE.mediumHigh
      else if (ratio > 0.4) fillColor = BLUE_SCALE.medium
      else if (ratio > 0.2) fillColor = BLUE_SCALE.mediumLow
      else fillColor = BLUE_SCALE.low
    }

    return {
      fillColor,
      weight: 0.5,
      opacity: 1,
      color: "#64748b", // slate-500
      fillOpacity: 0.8,
    }
  }, [heatmapData, metric])

  const onEachFeature = useCallback((feature, layer) => {
    const cityName = feature.properties.name
    const dataItem = heatmapData.find(d => d._id?.toLowerCase() === cityName?.toLowerCase())

    if (dataItem) {
      const tooltipContent = `
        <div class="text-sm">
          <p class="font-bold">${cityName}</p>
          <p>District: ${dataItem.district}</p>
          <hr class="my-1 border-gray-200"/>
          <p>Orders: ${dataItem.orderCount}</p>
          <p>Sales: Rs. ${dataItem.totalSales.toLocaleString()}</p>
          <p>Avg Delivery: Rs. ${dataItem.avgDeliveryCost}</p>
        </div>
      `
      layer.bindTooltip(tooltipContent, { sticky: true, className: "custom-leaflet-tooltip" })

      layer.on({
        mouseover: (e) => {
          const l = e.target
          l.setStyle({ weight: 2, color: "#334155", fillOpacity: 1 })
          l.bringToFront()
        },
        mouseout: (e) => {
          const l = e.target
          // geoJson layer will reset style automatically if using function reference in style prop? 
          // actually easier to just manually reset if needed, but react-leaflet handles updates well usually.
          // For simplicity let's rely on re-render or basic reset:
          l.setStyle({ weight: 0.5, color: "#64748b", fillOpacity: 0.8 })
        }
      })
    }
  }, [heatmapData])

  // --- Heatmap Points Calculation ---
  const heatmapPoints = useMemo(() => {
    if (!geoJsonData || !heatmapData.length) return []

    // Helper to calculate polygon centroid
    const getCentroid = (coords) => {
      // Simple average of points (works well enough for small city polygons)
      // Coords structure for Polygon: [[[lng, lat], ...]]
      // We only take the first ring [0]
      if (!coords || !coords[0]) return null

      let latSum = 0, lngSum = 0, count = 0
      coords[0].forEach(pt => {
        lngSum += pt[0]
        latSum += pt[1]
        count++
      })
      return [latSum / count, lngSum / count]
    }

    const points = []

    geoJsonData.features.forEach(feature => {
      const cityName = feature.properties.name
      const dataItem = heatmapData.find(d => d._id?.toLowerCase() === cityName?.toLowerCase())

      if (dataItem && dataItem[metric] > 0) {
        const centroid = getCentroid(feature.geometry.coordinates)
        if (centroid) {
          points.push([centroid[0], centroid[1], dataItem[metric]])
        }
      }
    })
    return points
  }, [geoJsonData, heatmapData, metric])


  if (loading && !heatmapData.length) return (
    <div className="flex items-center justify-center h-screen text-gray-500">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-2"></div>
      Loading Intelligence...
    </div>
  )

  if (error) return (
    <div className="p-8 flex flex-col items-center justify-center h-screen text-red-500">
      <AlertCircle className="w-12 h-12 mb-4" />
      <p className="text-lg font-semibold">{error}</p>
      <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-red-100 rounded-md hover:bg-red-200">Retry</button>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <MapPin className="mr-2 text-blue-600" /> geospatial Intelligence
          </h1>
          <p className="text-sm text-gray-500 mt-1">Analyze order distribution and sales performance across Sri Lanka.</p>
        </div>

        {/* Global Controls */}
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 flex flex-wrap gap-4 items-end">
          <FilterSelect
            label="Date Range"
            value={dateRange}
            onChange={setDateRange}
            options={[
              { label: "Last 7 Days", value: "7" },
              { label: "Last 30 Days", value: "30" },
              { label: "Last 90 Days", value: "90" },
              { label: "All Time", value: "3650" }, // ~10 years
              { label: "Custom", value: "custom" }
            ]}
          />
          {dateRange === "custom" && (
            <>
              <div className="flex flex-col">
                <label className="text-xs font-semibold text-gray-500 mb-1">Start</label>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="border rounded-md p-1.5 text-sm" />
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-semibold text-gray-500 mb-1">End</label>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="border rounded-md p-1.5 text-sm" />
              </div>
            </>
          )}
          <FilterSelect
            label="Map Metric"
            value={metric}
            onChange={setMetric}
            options={[
              { label: "Order Count", value: "orderCount" },
              { label: "Total Sales", value: "totalSales" },
              { label: "Avg Delivery Rate", value: "avgDeliveryCost" },
            ]}
          />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Orders"
          value={stats.totalOrders.toLocaleString()}
          icon={<Package />}
          subtext="In selected period"
          bgClass="bg-blue-50"
          colorClass="text-blue-600"
        />
        <StatCard
          title="Total Revenue"
          value={`Rs. ${stats.totalSales.toLocaleString()}`}
          icon={<DollarSign />}
          subtext="Gross sales"
          bgClass="bg-green-50"
          colorClass="text-green-600"
        />
        <StatCard
          title="Avg. Order Value"
          value={`Rs. ${stats.avgOrderValue.toLocaleString()}`}
          icon={<TrendingUp />}
          subtext="Per successful order"
          bgClass="bg-purple-50"
          colorClass="text-purple-600"
        />
      </div>

      {/* Main Content Areas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Map Section (2/3 width) */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col h-[600px]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-gray-400" /> Regional Distribution
            </h3>
            {/* Map Mode Toggle */}
            <div className="flex bg-gray-100 p-1 rounded-md">
              <button
                className={`px-3 py-1 text-xs font-medium rounded-sm transition-all ${mapMode === "choropleth" ? "bg-white shadow text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                onClick={() => setMapMode("choropleth")}
              >
                Areach Chart
              </button>
              <button
                className={`px-3 py-1 text-xs font-medium rounded-sm transition-all ${mapMode === "heatmap" ? "bg-white shadow text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                onClick={() => setMapMode("heatmap")}
              >
                Heatmap (Density)
              </button>
            </div>
          </div>

          <div className="flex-grow rounded-lg overflow-hidden border border-gray-100 relative bg-slate-50">
            {geoJsonData ? (
              <MapContainer
                center={INITIAL_CENTER}
                zoom={INITIAL_ZOOM}
                style={{ height: "100%", width: "100%" }}
                minZoom={7}
                maxBounds={SRI_LANKA_BOUNDS}
              >
                <TileLayer
                  url={mapMode === "heatmap"
                    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" // Dark map for heatmap
                    : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" // Light map for choropleth
                  }
                  attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                />

                {mapMode === "heatmap" ? (
                  <HeatmapLayer points={heatmapPoints} />
                ) : (
                  <GeoJSON
                    key={`geo-${metric}-${dateRange}`}
                    data={geoJsonData}
                    style={getFeatureStyle}
                    onEachFeature={onEachFeature}
                  />
                )}

              </MapContainer>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">Loading Map Data...</div>
            )}
          </div>
        </div>

        {/* Sidebar Charts (1/3 width) */}
        <div className="flex flex-col gap-6">

          {/* Top Districts Donut */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-[320px]">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Top Districts by Orders</h3>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={districtData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {districtData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Cities List (replacing Bar chart for better compactness) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex-grow overflow-auto h-[256px]">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Top Performing Cities</h3>
            <div className="space-y-3">
              {topCities.map((city, idx) => (
                <div key={idx} className="flex items-center justify-between border-b border-gray-50 pb-2 last:border-0">
                  <div className="flex items-center">
                    <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold mr-3">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{city._id}</p>
                      <p className="text-xs text-gray-500">{city.district}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-700">Rs. {(city.totalSales / 1000).toFixed(1)}k</p>
                    <p className="text-xs text-gray-400">{city.orderCount} Orders</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>

      {/* Sales Trend (Bottom Full Width) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-[400px]">
        <h3 className="text-lg font-semibold text-gray-700 mb-6">Sales Performance Trends</h3>
        <ResponsiveContainer width="100%" height="85%">
          <AreaChart data={trendChartData}>
            <defs>
              <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} dy={10} />
            <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} tickFormatter={(val) => `Rs.${val / 1000}k`} />
            <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
            <RechartsTooltip
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              formatter={(value, name) => [name === 'Sales' ? `Rs. ${value.toLocaleString()}` : value, name]}
            />
            <Legend iconType="circle" />
            <Area yAxisId="left" type="monotone" dataKey="Sales" stroke="#8884d8" fillOpacity={1} fill="url(#colorSales)" strokeWidth={2} />
            <Area yAxisId="right" type="monotone" dataKey="Orders" stroke="#82ca9d" fillOpacity={1} fill="url(#colorOrders)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

    </div>
  )
}

export default OrderHeatMap
